import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { Observable, catchError, combineLatest, defer, map, of, startWith } from 'rxjs';
import { ProductPromiseService } from '../../product-promise.service';

const ALL_METHODS_SENTINEL = '__ALL_METHODS__';

@Component({
  selector: 'shipping-method-line-component-editor',
  templateUrl: './shipping-method-line-component-editor.component.html',
  styleUrls: ['./shipping-method-line-component-editor.component.scss'],
  standalone: false,
})
export class ShippingMethodLineComponentEditorComponent implements OnInit {
  @Input() componentForm: UntypedFormGroup;

  optionsForm: UntypedFormGroup;
  initialButtonLabel: string;
  allowedMethodOptions$: Observable<DropdownOption[]>;

  readonly priceDisplayOptions: DropdownOption[] = [
    {
      label: 'Show absolute shipping cost in brackets',
      value: ShopPlugin.ProductPagePromise.ShippingMethodLinePriceDisplay.ABSOLUTE_IN_BRACKETS,
    },
    {
      label: "Don't show price",
      value: ShopPlugin.ProductPagePromise.ShippingMethodLinePriceDisplay.HIDDEN,
    },
  ];

  private destroyRef = inject(DestroyRef);

  constructor(
    private productPromiseService: ProductPromiseService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.optionsForm = this.componentForm.get('options') as UntypedFormGroup;
    this.initialButtonLabel = this.optionsForm.get('buttonLabel')?.value ?? '';

    const allowedNames$ = defer(() =>
      this.optionsForm
        .get('allowedMethodNames')!
        .valueChanges.pipe(startWith<string[]>(this.optionsForm.get('allowedMethodNames')?.value ?? [])),
    );
    const allowAllMethods$ = defer(() =>
      this.optionsForm
        .get('allowAllMethods')!
        .valueChanges.pipe(startWith<boolean>(!!this.optionsForm.get('allowAllMethods')?.value)),
    );
    const enabledNames$ = this.productPromiseService.listEnabledShippingMethodNames().pipe(
      catchError((err) => {
        console.error(err);
        this.toastService.showToast({ mode: 'danger', message: 'Failed to load shipping methods' });
        return of([] as string[]);
      }),
    );
    this.allowedMethodOptions$ = combineLatest([enabledNames$, allowedNames$, allowAllMethods$]).pipe(
      map(([names, currentAllowed, allowAll]) => [
        {
          label: 'All Delivery Methods',
          value: ALL_METHODS_SENTINEL,
          checked: !!allowAll,
        },
        ...names.map((name) => ({
          label: name,
          value: name,
          checked: !allowAll && (currentAllowed ?? []).includes(name),
          disabled: !!allowAll,
        })),
      ]),
    );

    this.syncAllowedMethodsValidator(this.optionsForm.get('allowAllMethods')?.value);
    this.optionsForm
      .get('allowAllMethods')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allMethods: boolean) => this.syncAllowedMethodsValidator(allMethods));
  }

  get currentPriceDisplay(): DropdownOption {
    const value = this.optionsForm?.get('priceDisplay')?.value;
    return this.priceDisplayOptions.find((option) => option.value === value) ?? this.priceDisplayOptions[0];
  }

  onAllowedMethodsChange(allOptions: DropdownOption[]): void {
    const allRow = (allOptions ?? []).find((option) => option.value === ALL_METHODS_SENTINEL);
    const newAllowAll = !!allRow?.checked;
    const currentAllowAll = !!this.optionsForm.get('allowAllMethods').value;
    if (newAllowAll !== currentAllowAll) {
      this.optionsForm.get('allowAllMethods').setValue(newAllowAll);
    } else {
      const methodRows = (allOptions ?? []).filter((option) => option.value !== ALL_METHODS_SENTINEL);
      const names = methodRows.filter((option) => option.checked).map((option) => option.value as string);
      this.optionsForm.get('allowedMethodNames').setValue(names);
    }
    this.optionsForm.markAsDirty();
  }

  onButtonLabelChange(html: string): void {
    this.optionsForm.get('buttonLabel').setValue(html);
    this.optionsForm.markAsDirty();
  }

  private syncAllowedMethodsValidator(allMethods: boolean): void {
    const control = this.optionsForm.get('allowedMethodNames');
    if (!control) return;
    if (!allMethods) {
      control.setValidators([Validators.required, Validators.minLength(1)]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }
}
